import React, { useState, useRef, useEffect } from 'react';
import { Modal, Box, Typography, Button, CircularProgress, TextField, FormControl, Checkbox, FormControlLabel, FormGroup, FormLabel, LinearProgress, Chip } from '@mui/material';
import { ICategory } from '../types/damTypes';
import axiosInstance from '../services/axiosInstance';
import {
  buildUploadTooLargeMessage,
  formatUploadBytes,
  formatUploadEta,
  formatUploadSpeed,
  getUploadErrorMessage,
  splitFilesByUploadSize,
} from '../utils/uploadUi';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
  categories: ICategory[];
  loadingCategories: boolean;
}

type UploadPhase = 'idle' | 'uploading' | 'processing' | 'completed' | 'error';

type UploadStats = {
  uploadedBytes: number;
  totalBytes: number;
  speedBps: number;
  etaSeconds?: number;
  phase: UploadPhase;
};

const createInitialUploadStats = (): UploadStats => ({
  uploadedBytes: 0,
  totalBytes: 0,
  speedBps: 0,
  etaSeconds: undefined,
  phase: 'idle',
});

function UploadModal({ open, onClose, onUploadSuccess, categories, loadingCategories }: UploadModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState<UploadStats>(createInitialUploadStats);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const incomingFiles = event.target.files;
    if (!incomingFiles || incomingFiles.length === 0) {
      setSelectedFiles([]);
      setMessage('');
      return;
    }

    const { acceptedFiles, rejectedFiles } = splitFilesByUploadSize(incomingFiles);
    setSelectedFiles(acceptedFiles);
    setMessage(rejectedFiles.length > 0 ? buildUploadTooLargeMessage(rejectedFiles) : '');

    if (acceptedFiles.length > 0) {
      const fileName = acceptedFiles[0].name.split(/(\\|\/)/).pop() || '';
      setTitle(fileName.replace(/\.[^/\\]+$/, ''));
    } else {
      setTitle('');
    }
  };

  const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const categoryId = event.target.value;
    setSelectedCategoryIds(prevSelectedIds => {
      if (prevSelectedIds.includes(categoryId)) {
        return prevSelectedIds.filter(id => id !== categoryId);
      } else {
        return [...prevSelectedIds, categoryId];
      }
    });
  };

  const handleUpload = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (selectedFiles.length === 0) {
      setMessage('Välj minst en fil att ladda upp.');
      return;
    }

    setUploading(true);
    setMessage('');
    setUploadProgress(0);
    const totalBytes = selectedFiles.reduce((sum, file) => sum + (file.size || 0), 0);
    const uploadStartTime = Date.now();
    setUploadStats({
      uploadedBytes: 0,
      totalBytes,
      speedBps: 0,
      etaSeconds: undefined,
      phase: 'uploading',
    });

    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append('asset', selectedFiles[i]);
    }

    formData.append('title', title);
    formData.append('description', description);
    formData.append('tags', tags);
    
    // Log selected categories before appending
    console.log('Selected category IDs to append:', selectedCategoryIds);
    
    // Clear any existing category values to avoid duplicates
    // Then append each category ID individually
    if (selectedCategoryIds.length > 0) {
      selectedCategoryIds.forEach(id => {
        formData.append('categories', id);
        console.log('Appended category:', id);
      });
    }

    // Debug what's in the FormData
    console.log('FormData entries:');
    // Use alternative approach to inspect FormData without iteration
    console.log('Files count:', selectedFiles?.length || 0);
    console.log('Title:', formData.get('title'));
    console.log('Description:', formData.get('description'));
    console.log('Tags:', formData.get('tags'));
    console.log('Categories:', selectedCategoryIds);

    try {
      const response = await axiosInstance.post('/api/assets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          const elapsedMs = Math.max(1, Date.now() - uploadStartTime);
          const speedBps = (progressEvent.loaded * 1000) / elapsedMs;
          const remainingBytes = Math.max(0, totalBytes - progressEvent.loaded);
          const etaSeconds = speedBps > 0 ? Math.round(remainingBytes / speedBps) : undefined;
          const phase: UploadPhase = progressEvent.loaded >= progressEvent.total ? 'processing' : 'uploading';
          setUploadProgress(progress);
          setUploadStats({
            uploadedBytes: progressEvent.loaded,
            totalBytes,
            speedBps,
            etaSeconds,
            phase,
          });
        },
      });

      const uploadedAssets = (Array.isArray(response?.data) ? response.data : [response?.data]) as Array<{
        _id?: string;
        categories?: string[];
      }>;
      const uploadedAssetIds = Array.from(new Set(
        uploadedAssets
          .map((asset) => asset?._id ? String(asset._id) : '')
          .filter(Boolean),
      ));
      const uploadedCategoryIds = Array.from(new Set(
        uploadedAssets.flatMap((asset) =>
          Array.isArray(asset?.categories)
            ? asset.categories.map((categoryId) => String(categoryId))
            : [],
        ),
      ));
      
      setMessage(`Uppladdning klar: ${response.data.length || 1} filer uppladdade.`);
      setSelectedFiles([]);
      setUploadProgress(100);
      setUploadStats({
        uploadedBytes: totalBytes,
        totalBytes,
        speedBps: 0,
        etaSeconds: 0,
        phase: 'completed',
      });
      setTitle('');
      setDescription('');
      setTags('');
      setSelectedCategoryIds([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Close modal first
      onClose();

      onUploadSuccess?.();

      window.setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent('dam:upload-batch-completed', {
            detail: {
              assetIds: uploadedAssetIds,
              categoryIds: uploadedCategoryIds,
            },
          }));
        } catch {}
      }, 100);
    } catch (error: any) {
      console.error('Upload error:', error);
      setMessage(`Uppladdningen misslyckades: ${getUploadErrorMessage(error)}`);
      setUploadStats(prev => ({
        ...prev,
        speedBps: 0,
        etaSeconds: undefined,
        phase: 'error',
      }));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedFiles([]);
      setUploading(false);
      setMessage('');
      setUploadProgress(0);
      setUploadStats(createInitialUploadStats());
      setTitle('');
      setDescription('');
      setTags('');
      setSelectedCategoryIds([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="upload-modal-title"
      aria-describedby="upload-modal-description"
    >
      <Box component="form" onSubmit={handleUpload} sx={{
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>
        <Typography id="upload-modal-title" variant="h6" component="h2" gutterBottom>
          Ladda upp ny tillgång
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <input
            type="file"
            multiple
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          <Button
            variant="contained"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Välj fil{selectedFiles.length > 0 ? ` (${selectedFiles.length})` : 'er'}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={uploading || selectedFiles.length === 0}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {uploading ? 'Laddar upp...' : 'Ladda upp'}
          </Button>
        </Box>

        {(uploading || uploadStats.phase === 'completed' || uploadStats.phase === 'error') && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip
                size="small"
                color={
                  uploadStats.phase === 'completed'
                    ? 'success'
                    : uploadStats.phase === 'error'
                      ? 'error'
                      : uploadStats.phase === 'processing'
                        ? 'info'
                        : 'primary'
                }
                label={
                  uploadStats.phase === 'completed'
                    ? 'Klar'
                    : uploadStats.phase === 'error'
                      ? 'Misslyckades'
                      : uploadStats.phase === 'processing'
                        ? 'Bearbetar på servern'
                        : 'Laddar upp'
                }
              />
              <Typography variant="body2" color="text.secondary">
                {formatUploadBytes(uploadStats.uploadedBytes)} / {formatUploadBytes(uploadStats.totalBytes)}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 10, borderRadius: 5 }} />
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2">{uploadProgress}% klart</Typography>
              <Typography variant="body2" color="text.secondary">
                {uploadStats.phase === 'processing'
                  ? 'Servern bearbetar filen...'
                  : `Fart: ${formatUploadSpeed(uploadStats.speedBps)} • ETA: ${formatUploadEta(uploadStats.etaSeconds)}`}
              </Typography>
            </Box>
          </Box>
        )}

        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component="h4" gutterBottom>
              Valda filer:
            </Typography>
            <ul style={{ wordBreak: 'break-all', paddingLeft: '20px' }}>
              {selectedFiles.map((file, index) => (
                <li key={index}>
                  {file.name.split(/(\\|\/)/).pop()} ({formatUploadBytes(file.size)})
                </li>
              ))}
            </ul>
          </Box>
        )}

        {message && <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>{message}</Typography>}

        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" component="h4" gutterBottom>
              Metadata
            </Typography>
            <TextField
                label="Titel"
                fullWidth
                margin="normal"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={uploading}
            />
            <TextField
                label="Beskrivning"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={uploading}
            />
             <TextField
                label="Taggar (kommaseparerade)"
                fullWidth
                margin="normal"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                disabled={uploading}
            />

            {categories.length > 0 && (
                <FormControl component="fieldset" margin="normal" disabled={uploading || loadingCategories}>
                    <FormLabel component="legend">Kategorier</FormLabel>
                    <FormGroup>
                        {/* Main categories */}
                        {categories
                            .filter(category => !category.parentCategory)
                            .map(mainCategory => (
                                <Box key={mainCategory._id} sx={{ mb: 1 }}>
                                    {/* Main category checkbox */}
                                    <FormControlLabel
                                        control={
                                            <Checkbox 
                                                checked={selectedCategoryIds.includes(mainCategory._id)}
                                                onChange={handleCategoryChange}
                                                value={mainCategory._id}
                                            />
                                        }
                                        label={<Typography sx={{ fontWeight: 'bold' }}>{mainCategory.name}</Typography>}
                                    />
                                    
                                    {/* Subcategories for this main category */}
                                    <Box sx={{ ml: 4 }}>
                                        {categories
                                            .filter(subCategory => {
                                                if (typeof subCategory.parentCategory === 'string') {
                                                    return subCategory.parentCategory === mainCategory._id;
                                                } else if (subCategory.parentCategory && typeof subCategory.parentCategory === 'object') {
                                                    return (subCategory.parentCategory as {_id: string})._id === mainCategory._id;
                                                }
                                                return false;
                                            })
                                            .map(subCategory => (
                                                <FormControlLabel
                                                    key={subCategory._id}
                                                    control={
                                                        <Checkbox 
                                                            checked={selectedCategoryIds.includes(subCategory._id)}
                                                            onChange={handleCategoryChange}
                                                            value={subCategory._id}
                                                        />
                                                    }
                                                    label={subCategory.name}
                                                />
                                            ))
                                        }
                                    </Box>
                                </Box>
                            ))
                        }
                    </FormGroup>
                </FormControl>
            )}
            {categories.length === 0 && !loadingCategories && (
                 <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Inga kategorier tillgängliga för val.</Typography>
             )}
             {loadingCategories && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Laddar kategorier...</Typography>
             )}

        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={onClose} color="secondary" disabled={uploading}>
                Avbryt
            </Button>
        </Box>

      </Box>
    </Modal>
  );
}

export default UploadModal; 